import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getAllPendingRecords, saveRecord } from '@/services/storage/local-attendance-storage'
import connectivityService from '@/services/connectivity/connectivity'
import { syncPendingRecords as batchSyncRecords } from '@/services/api/sync'
import syncRetryService from '@/services/sync-retry'
import { logger } from '@/services/logger'

export interface SyncQueueItem {
    id: string
    action: 'check-in' | 'check-out'
    timestamp: string
    workerId: number
    location?: {
        latitude: number
        longitude: number
    }
    retryCount: number
}

export const useSyncStore = defineStore('sync', () => {
    const queue = ref<SyncQueueItem[]>([])
    const isSyncing = ref(false)
    const isOffline = ref(false)
    const lastSyncTimestamp = ref<string | null>(null)
    const syncProgress = ref({ current: 0, total: 0 })
    const syncSummary = ref<{ synced: number; failed: number } | null>(null)

    const pendingCount = computed(() => queue.value.length)
    const syncProgressText = computed(() => {
        if (syncProgress.value.total === 0) return ''
        return `Syncing ${syncProgress.value.current} of ${syncProgress.value.total} records...`
    })

    function addToQueue(item: Omit<SyncQueueItem, 'id' | 'retryCount'>) {
        queue.value.push({
            ...item,
            id: crypto.randomUUID(),
            retryCount: 0
        })
    }

    function removeFromQueue(id: string) {
        queue.value = queue.value.filter(item => item.id !== id)
    }

    function incrementRetry(id: string) {
        const item = queue.value.find(item => item.id === id)
        if (item) {
            item.retryCount++
        }
    }

    function setIsSyncing(syncing: boolean) {
        isSyncing.value = syncing
    }

    function setLastSyncTimestamp(timestamp: string) {
        lastSyncTimestamp.value = timestamp
    }

    function clearQueue() {
        queue.value = []
    }

    function setIsOffline(offline: boolean) {
        isOffline.value = offline
    }

    // Store cleanup handlers for manual cleanup if needed
    let cleanupHandlers: (() => void)[] = []

    /**
     * Initialize network status listener with ConnectivityService
     * Also sets up app lifecycle listeners for foreground sync
     */
    async function initializeNetworkListener() {
        try {
            // Initialize ConnectivityService
            await connectivityService.initialize()

            // Get initial network status
            const status = connectivityService.getStatus()
            isOffline.value = !status.connected

            // Listen for network status changes
            connectivityService.addListener(status => {
                const wasOffline = isOffline.value
                isOffline.value = !status.connected

                // Trigger sync when transitioning from offline to online
                if (wasOffline && !isOffline.value) {
                    logger.info('Network connectivity restored - triggering sync')
                    syncPendingRecords()
                }
            })

            // Listen for app resume/foreground events
            const handleAppResume = async () => {
                if (!isOffline.value) {
                    logger.info('App resumed with connectivity - triggering sync')
                    await syncPendingRecords()
                }
            }

            // Listen for retry events
            const handleRetryDue = async (event: Event) => {
                const customEvent = event as CustomEvent<{ recordIds: string[] }>
                logger.info(
                    `Retry scheduled for ${customEvent.detail.recordIds.length} records - triggering sync`
                )
                await syncPendingRecords()
            }

            // Add event listeners
            document.addEventListener('resume', handleAppResume)
            window.addEventListener('sync-retry-due', handleRetryDue)

            // Store cleanup handlers
            cleanupHandlers.push(() => {
                document.removeEventListener('resume', handleAppResume)
                window.removeEventListener('sync-retry-due', handleRetryDue)
            })
        } catch (error) {
            console.error('Failed to initialize network listener:', error)
            isOffline.value = false // Assume online if we can't detect
        }
    }

    /**
     * Cleanup event listeners (call this if you need to cleanup the store manually)
     */
    function cleanup() {
        cleanupHandlers.forEach(handler => handler())
        cleanupHandlers = []
    }

    /**
     * Sync pending records to the server
     * Uses batch sync endpoint to upload all pending offline records at once
     */
    async function syncPendingRecords() {
        // Prevent concurrent sync operations
        if (isSyncing.value) {
            logger.info('Sync already in progress, skipping')
            return
        }

        // Don't sync if offline
        if (isOffline.value) {
            logger.info('Device is offline, skipping sync')
            return
        }

        try {
            setIsSyncing(true)
            syncSummary.value = null

            // Load pending records from storage
            const pendingRecords = await getAllPendingRecords()

            if (pendingRecords.length === 0) {
                logger.info('No pending records to sync')
                setLastSyncTimestamp(new Date().toISOString())
                return
            }

            logger.info(`Syncing ${pendingRecords.length} pending records via batch endpoint`)

            // Update progress
            syncProgress.value = { current: 0, total: pendingRecords.length }

            // Call batch sync API with LocalAttendanceRecord
            const response = await batchSyncRecords(pendingRecords)

            // Update progress during processing
            syncProgress.value = { current: pendingRecords.length, total: pendingRecords.length }

            // Process sync results
            const failedErrors = new Map(response.errors.map(e => [e.recordId, e.error]))

            for (const record of pendingRecords) {
                const error = failedErrors.get(record.id)
                if (error) {
                    // Log error
                    logger.error(`Failed to sync record ${record.id}: ${error}`)

                    // Persist failure state so backoff and retry count survive app restarts
                    const newRetryCount = (record.retryCount || 0) + 1
                    try {
                        await saveRecord({ ...record, retryCount: newRetryCount, errorMessage: error })
                    } catch (saveError) {
                        logger.error(`Failed to persist retry count for record ${record.id}:`, saveError as Error)
                    }

                    // Schedule retry with exponential backoff
                    syncRetryService.scheduleRetry(record.id, newRetryCount)
                } else {
                    // Successfully synced - record is already marked as 'synced' by sync service
                    // No need to delete - it's now a synced record in local DB
                    logger.info(
                        `Record synced successfully: ${record.action} for worker ${record.workerId} (ID: ${record.id})`
                    )

                    // Remove from retry queue if it was there
                    syncRetryService.removeFromQueue(record.id)
                }
            }

            // Update summary
            syncSummary.value = { synced: response.synced, failed: response.failed }

            logger.info('Sync completed', {
                total: pendingRecords.length,
                synced: response.synced,
                failed: response.failed
            })

            setLastSyncTimestamp(new Date().toISOString())

            // Reload pending count to update UI
            await loadPendingCount()

            // Fetch history from server to ensure client has authoritative server state
            // Server may have resolved conflicts (earliest check-in, latest check-out)
            // This ensures server is the source of truth
            if (response.synced > 0) {
                try {
                    const { useUserStore } = await import('@/stores/user')
                    const { useAttendanceStore } = await import('@/stores/attendance')
                    const { fetchHistory, invalidateCache } = await import('@/services/api/history')
                    const userStore = useUserStore()
                    const attendanceStore = useAttendanceStore()

                    if (userStore.workerId) {
                        // Invalidate cache to force fresh fetch from server
                        invalidateCache(userStore.workerId)

                        logger.info('Fetching history from server to get authoritative state (with conflict resolution)')

                        // Fetch from server - this will:
                        // 1. Clear synced records (including ones we just synced)
                        // 2. Save server records (with conflict resolution applied)
                        // 3. Server state becomes source of truth
                        await fetchHistory(userStore.workerId)

                        // Refresh UI to show server state
                        await attendanceStore.refresh(userStore.workerId)

                        logger.info('Client now synchronized with server state')
                    }
                } catch (error) {
                    logger.error('Failed to fetch history after sync:', error as Error)
                    // Still try to refresh from local DB
                    const { useUserStore } = await import('@/stores/user')
                    const { useAttendanceStore } = await import('@/stores/attendance')
                    const userStore = useUserStore()
                    const attendanceStore = useAttendanceStore()
                    if (userStore.workerId) {
                        await attendanceStore.refresh(userStore.workerId)
                    }
                }
            }

            // Reset progress after a delay to show completion
            setTimeout(() => {
                syncProgress.value = { current: 0, total: 0 }
            }, 3000)
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            logger.error(`Sync process failed: ${errorMessage}`)
            syncProgress.value = { current: 0, total: 0 }
        } finally {
            setIsSyncing(false)
        }
    }

    /**
     * Load pending records count from offline storage
     */
    async function loadPendingCount() {
        try {
            const pendingRecords = await getAllPendingRecords()
            // Update queue with pending records (for display purposes)
            queue.value = pendingRecords.map(record => ({
                id: record.id,
                action: record.action,
                timestamp: record.checkInTime,
                workerId: record.workerId,
                location: {
                    latitude: record.location.lat,
                    longitude: record.location.lng
                },
                retryCount: record.retryCount || 0
            }))
        } catch (error) {
            console.error('Failed to load pending records:', error)
        }
    }

    return {
        queue,
        isSyncing,
        isOffline,
        lastSyncTimestamp,
        syncProgress,
        syncSummary,
        pendingCount,
        syncProgressText,
        addToQueue,
        removeFromQueue,
        incrementRetry,
        setIsSyncing,
        setIsOffline,
        setLastSyncTimestamp,
        clearQueue,
        initializeNetworkListener,
        loadPendingCount,
        syncPendingRecords,
        cleanup
    }
})
